import React, { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import type { CSSProperties } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

export interface SelectionDialogColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  sx?: SxProps<Theme>;
}

interface SelectionDialogProps<T> {
  open: boolean;
  title: string;
  rows: T[];
  columns: SelectionDialogColumn<T>[];
  onClose: () => void;
  onSelect: (row: T) => void;
  loading?: boolean;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  onSearch: () => void;
  onClear: () => void;
  searchLabel?: string;
  paperStyle?: CSSProperties;
  totalCount?: number;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

export function SelectionDialog<T>({
  open,
  title,
  rows,
  columns,
  onClose,
  onSelect,
  loading = false,
  searchValue,
  onSearchValueChange,
  onSearch,
  onClear,
  searchLabel = 'Search Table',
  paperStyle,
  totalCount,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: SelectionDialogProps<T>) {
  const [localPage, setLocalPage] = useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(5);
  const isServerPaginated = typeof totalCount === 'number' && typeof onPageChange === 'function' && typeof onRowsPerPageChange === 'function';
  const page = isServerPaginated ? (controlledPage ?? 0) : localPage;
  const rowsPerPage = isServerPaginated ? (controlledRowsPerPage ?? 5) : localRowsPerPage;

  useEffect(() => {
    if (!open) {
      setLocalPage(0);
      setLocalRowsPerPage(5);
    }
  }, [open]);

  useEffect(() => {
    if (isServerPaginated) {
      return;
    }

    const maxPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setLocalPage(maxPage);
    }
  }, [isServerPaginated, page, rows.length, rowsPerPage]);

  const pagedRows = useMemo(
    () => (isServerPaginated ? rows : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
    [isServerPaginated, rows, page, rowsPerPage],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        className: 'order-popup-dialog-paper order-selection-dialog-paper',
        style: {
          width: '1200px',
          maxWidth: '95vw',
          minHeight: '550px',
          ...paperStyle,
        },
      }}
    >
      <DialogTitle id="qa-title-lbl" sx={{ mb: 2.5 }}>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
          <TextField
            id="qa-searchTable-ipt"
            size="small"
            label={searchLabel}
            value={searchValue}
            onChange={(event) => onSearchValueChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onSearch();
              }
            }}
            sx={{ width: { xs: '100%', sm: '50%' } }}
          />
          <Button id="qa-search-btn" variant="contained" className="blue-button" onClick={onSearch}>SEARCH</Button>
          <Button id="qa-clear-btn" variant="contained" className="white-button" onClick={onClear}>CLEAR</Button>
        </Box>

        {loading ? (
          <Box sx={{ minHeight: 330, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress id="qa-spinner-ico" size={50} />
          </Box>
        ) : (
          <Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.align ?? 'left'} sx={column.sx}>
                      {column.header}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ pl: 0, pr: 0 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row, index) => (
                  <TableRow key={`${index}`} hover>
                    {columns.map((column) => (
                      <TableCell key={column.key} align={column.align ?? 'left'} sx={column.sx}>
                        {column.render(row) || '-'}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ pl: 0, pr: 0 }}>
                      <Button
                        id="qa-check-btn"
                        variant="outlined"
                        className="white-button"
                        onClick={() => onSelect(row)}
                      >
                        SELECT
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              id="qa-paginator"
              component="div"
              count={totalCount ?? rows.length}
              page={page}
              onPageChange={(_, nextPage) => {
                if (isServerPaginated) {
                  onPageChange(nextPage);
                  return;
                }

                setLocalPage(nextPage);
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                const nextRowsPerPage = Number(event.target.value);
                if (isServerPaginated) {
                  onRowsPerPageChange(nextRowsPerPage);
                  onPageChange(0);
                  return;
                }

                setLocalRowsPerPage(nextRowsPerPage);
                setLocalPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 100]}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button id="qa-cancel-btn" variant="contained" className="blue-button" onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
